/**
 * ToggleSwitch Component
 * 
 * A toggle switch with label and optional description, wrapped in a card-like container.
 * 
 * @param {string} label - Label text for the toggle
 * @param {string} description - Optional description below label
 * @param {boolean} checked - Whether the toggle is on
 * @param {function} onChange - Callback when toggle changes
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {boolean} withContainer - Wrap in a styled container (default: true)
 */

const COLOR_CLASSES = {
  blue: "bg-blue-600",
  rose: "bg-rose-600",
  amber: "bg-amber-600",
  indigo: "bg-indigo-600",
};

export default function ToggleSwitch({
  label,
  description = null,
  checked,
  onChange,
  color = "indigo",
  withContainer = true,
}) {
  const activeColor = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;

  const content = (
    <>
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{label}</h4>
        {description && (
          <p className="text-gray-500 text-xs mt-0.5">{description}</p>
        )}
      </div>
      
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`
          relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0
          ${checked ? activeColor : "bg-gray-300"}
        `}
      >
        <span
          className={`
            absolute left-0 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
            ${checked ? "translate-x-5" : "translate-x-0.5"}
          `}
        />
      </button>
    </>
  );

  if (withContainer) {
    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        {content}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4">
      {content}
    </div>
  );
}